/**
 * Store Synchronization Module
 *
 * Keeps the focused sub-stores (viewportStore, selectionStore, boardStore, itemStore)
 * in sync with canvasStore. canvasStore remains the source of truth for all state
 * mutations; the sub-stores provide fine-grained subscriptions so that, for example,
 * panning at 60fps only re-renders viewport consumers — not item or selection consumers.
 *
 * Call initStoreSync() once at app startup (e.g., in main.tsx) to activate:
 *
 *   import { initStoreSync } from '@/stores/storeSync';
 *   initStoreSync();
 */

import { useCanvasStore } from './canvasStore';
import { useViewportStore } from './viewportStore';
import { useSelectionStore } from './selectionStore';
import { useBoardStore } from './boardStore';
import { useItemStore } from './itemStore';

let initialized = false;

/**
 * Initialize store synchronization. Must be called after all stores are created.
 * Safe to call multiple times — only the first call has effect.
 */
export function initStoreSync() {
  if (initialized) return;
  initialized = true;

  // ---------------------------------------------------------------------------
  // One-way sync: canvasStore → sub-stores
  // ---------------------------------------------------------------------------

  useCanvasStore.subscribe((state) => {
    // Viewport
    useViewportStore.setState({
      panX: state.panX,
      panY: state.panY,
      zoom: state.zoom,
      viewportWidth: state.viewportWidth,
      viewportHeight: state.viewportHeight,
      shouldFitToView: state.shouldFitToView,
      isAutoOrganizing: state.isAutoOrganizing,
      isBoardTransitioning: state.isBoardTransitioning,
      lastBoardNavAt: state.lastBoardNavAt,
      activeTool: state.activeTool,
      hoverPreviewEnabled: state.hoverPreviewEnabled,
    });

    // Selection
    useSelectionStore.setState({
      selectedItemIds: state.selectedItemIds,
      newlyCreatedItemId: state.newlyCreatedItemId,
      isDraggingFromToolbar: state.isDraggingFromToolbar,
      draggedToolType: state.draggedToolType,
      dropTargetBoardId: state.dropTargetBoardId,
    });

    // Board
    useBoardStore.setState({
      boards: state.boards,
      currentBoardId: state.currentBoardId,
      boardPath: state.boardPath,
      boardShares: state.boardShares,
      viewMode: state.viewMode,
    });

    // Items
    useItemStore.setState({
      items: state.items,
      allItems: state.allItems,
      isLoading: state.isLoading,
      isSaving: state.isSaving,
      isEditingItem: state.isEditingItem,
      activeEditor: state.activeEditor,
      wallScrollOffset: state.wallScrollOffset,
      itemMinHeights: state.itemMinHeights,
      copiedItems: state.copiedItems,
      boardProfile: state.boardProfile,
      isProfilingBoard: state.isProfilingBoard,
      spatialClusters: state.spatialClusters,
      isClusteringEnabled: state.isClusteringEnabled,
      isDetectingClusters: state.isDetectingClusters,
      highlightedClusterId: state.highlightedClusterId,
      lastClusteredItemIds: state.lastClusteredItemIds,
    });
  });

  // ---------------------------------------------------------------------------
  // Initialize sub-stores with current canvasStore state
  // ---------------------------------------------------------------------------

  const initial = useCanvasStore.getState();

  useViewportStore.setState({
    panX: initial.panX,
    panY: initial.panY,
    zoom: initial.zoom,
    viewportWidth: initial.viewportWidth,
    viewportHeight: initial.viewportHeight,
    shouldFitToView: initial.shouldFitToView,
    isAutoOrganizing: initial.isAutoOrganizing,
    isBoardTransitioning: initial.isBoardTransitioning,
    lastBoardNavAt: initial.lastBoardNavAt,
    activeTool: initial.activeTool,
    hoverPreviewEnabled: initial.hoverPreviewEnabled,
  });

  useSelectionStore.setState({
    selectedItemIds: initial.selectedItemIds,
    newlyCreatedItemId: initial.newlyCreatedItemId,
    isDraggingFromToolbar: initial.isDraggingFromToolbar,
    draggedToolType: initial.draggedToolType,
    dropTargetBoardId: initial.dropTargetBoardId,
  });

  useBoardStore.setState({
    boards: initial.boards,
    currentBoardId: initial.currentBoardId,
    boardPath: initial.boardPath,
    boardShares: initial.boardShares,
    viewMode: initial.viewMode,
  });

  useItemStore.setState({
    items: initial.items,
    allItems: initial.allItems,
    isLoading: initial.isLoading,
    isSaving: initial.isSaving,
    isEditingItem: initial.isEditingItem,
    activeEditor: initial.activeEditor,
    wallScrollOffset: initial.wallScrollOffset,
    itemMinHeights: initial.itemMinHeights,
    copiedItems: initial.copiedItems,
    boardProfile: initial.boardProfile,
    isProfilingBoard: initial.isProfilingBoard,
    spatialClusters: initial.spatialClusters,
    isClusteringEnabled: initial.isClusteringEnabled,
    isDetectingClusters: initial.isDetectingClusters,
    highlightedClusterId: initial.highlightedClusterId,
    lastClusteredItemIds: initial.lastClusteredItemIds,
  });
}
